/**
 * The organisation's data, taken away (PRD 13.4): the people, every card of
 * every piece of work, and its own templates, for HR, admins and compliance.
 * Each file is fetched by a POST the worker logs before answering, then
 * handed to the browser to save. The audit log is exported from its own
 * search, by admins and compliance alone (PRD 11.2).
 */

import { useState } from "react";
import { canExportAudit, canExportPrivate, type DataExport } from "@utuwrk/core";
import { sentence } from "../lib/api.js";
import { save } from "../lib/download.js";
import { useOrg } from "../lib/org.js";
import { plural } from "../lib/words.js";
import { Link, PageHeader, Problem } from "./bits.js";

const FILES: { what: DataExport; title: string; body: string }[] = [
  {
    what: "employees",
    title: "Employees (CSV)",
    body: "Everybody utuwrk has onboarded or offboarded: where they are, their site, department, manager and dates. Nothing private.",
  },
  {
    what: "work",
    title: "Work history (CSV)",
    body: "Every card of every onboarding, offboarding and access change, one row each: its state, when it was due and finished, who finished it, and what it recorded. Private values read [PII].",
  },
  {
    what: "templates",
    title: "Templates (JSON)",
    body: "The organisation's own templates, every saved version, cards, conditions and branches included. The starters are utuwrk's and not included.",
  },
];

export function Exports() {
  const { api, org } = useOrg();
  const role = { role: org.role };
  const [busy, setBusy] = useState<DataExport | null>(null);
  const [withPrivate, setWithPrivate] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function take(what: DataExport) {
    setBusy(what);
    setProblem(null);
    setDone(null);
    try {
      const file = await api.dataExport(what, what === "work" && withPrivate);
      save(file.filename, file.text, file.type);
      setDone(
        `Exported ${file.filename}: ${plural(file.rows, what === "templates" ? "template" : "row")}. The export is in the audit log.`,
      );
    } catch (failure) {
      setProblem(sentence(failure));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="page">
      <PageHeader
        title="Export data"
        meta="Your organisation's data, whenever you want it. Every export is recorded in the audit log."
      />
      <Problem>{problem}</Problem>
      {done ? (
        <p className="pill s-sent" role="status">
          {done}
        </p>
      ) : null}
      {FILES.map((file) => (
        <div key={file.what} className="panel">
          <h2 className="section-title">{file.title}</h2>
          <p className="muted">{file.body}</p>
          {file.what === "work" && canExportPrivate(role) ? (
            <label className="check">
              <input
                type="checkbox"
                checked={withPrivate}
                onChange={(event) => setWithPrivate(event.target.checked)}
              />
              <span>Include private values (logged as a view of every one)</span>
            </label>
          ) : null}
          <div className="action-row">
            <button
              type="button"
              className="button button-quiet"
              disabled={busy !== null}
              onClick={() => void take(file.what)}
            >
              {busy === file.what ? "Exporting…" : "Export"}
            </button>
          </div>
        </div>
      ))}
      <div className="panel">
        <h2 className="section-title">Audit log (CSV)</h2>
        <p className="muted">
          {canExportAudit(role)
            ? "Search it, then export what you found, from the audit log."
            : "Searched in the audit log; exported by an admin or a compliance officer."}
        </p>
        <div className="action-row">
          <Link to={{ name: "log" }} className="button button-quiet">
            Open the audit log
          </Link>
        </div>
      </div>
    </section>
  );
}
